// ---------------------------------------------------------------------------
// Reading players out of client state. Seat order comes from `playerOrder`,
// never from the array order, which is not guaranteed to be seat order.
// ---------------------------------------------------------------------------

import type { ClientGameState, PublicPlayer, Team } from '@countdown/shared';

export function playerById(state: ClientGameState, id: string | null): PublicPlayer | null {
  if (id === null) return null;
  return state.players.find((player) => player.id === id) ?? null;
}

function inSeatOrder(state: ClientGameState, players: PublicPlayer[]): PublicPlayer[] {
  const position = new Map(state.playerOrder.map((id, index) => [id, index]));
  // Anyone missing from playerOrder sorts last rather than to the front, which
  // is what a -1 would do.
  const rank = (player: PublicPlayer) => position.get(player.id) ?? Number.MAX_SAFE_INTEGER;
  return [...players].sort((a, b) => rank(a) - rank(b));
}

export function seatedPlayers(state: ClientGameState): PublicPlayer[] {
  return inSeatOrder(
    state,
    state.players.filter((player) => player.role === 'player'),
  );
}

export function spectators(state: ClientGameState): PublicPlayer[] {
  return state.players.filter((player) => player.role === 'spectator');
}

export function teamOf(state: ClientGameState, player: PublicPlayer): Team | null {
  if (player.teamId === null) return null;
  return state.teams.find((team) => team.id === player.teamId) ?? null;
}

export function membersOfTeam(state: ClientGameState, teamId: string): PublicPlayer[] {
  return seatedPlayers(state).filter((player) => player.teamId === teamId);
}

/**
 * The colour to draw a player in. Teams own colour outright: while teams are on,
 * a player on a team wears their team's colour and not their own.
 *
 * Two colour systems competing for the same swatch is what made a teams game
 * unreadable — you could not tell at a glance whether a colour meant "this
 * person" or "this side". Anything that renders a player must come through here
 * rather than reaching for `player.color`, or the two systems drift apart again.
 *
 * A player with no team keeps their own colour, which the server draws from a
 * palette disjoint from the team palette, so an unteamed player can never be
 * mistaken for a side.
 *
 * **Null is a real answer and every caller has to draw it.** Past
 * `COLORED_SEATS` seats nobody in the room has a colour at all — see
 * `colorForSeat` in `constants.ts`, which makes that decision once and makes it
 * all-or-nothing — so this returns null rather than substituting a grey, because
 * a grey is itself a colour and reads as an identity. Above that line the room
 * is told apart by name. A team always has one, so a teamed player in a large
 * room still wears their side's.
 */
export function displayColorOf(state: ClientGameState, player: PublicPlayer): string | null {
  if (state.teams.length === 0) return player.color;
  return teamOf(state, player)?.color ?? player.color;
}

/**
 * The player's score bucket total: their team's when they are on a team, their
 * own when they are not.
 *
 * `teamScores` is keyed the way the engine buckets scores — a team id for a
 * teamed player, a player id for a solo one — and a team's total is best-of, not
 * a sum, so it can never be reconstructed by adding members up.
 */
export function scoreFor(state: ClientGameState, player: PublicPlayer): number {
  const key = player.teamId ?? player.id;
  return state.teamScores[key] ?? player.score;
}

/**
 * What a bot's badge says, and the one implementation of it.
 *
 * **An AI opponent is `ai`, never its difficulty, and it read `medium` on the
 * end screen for as long as this rule lived in two places.** Every AI seat is
 * seated at `medium` — `botDifficulty` is the percentile sampling a heuristic
 * bot is built out of, and a model does not have one, so the field is carried
 * at its default rather than left null. Reading it without asking `botKind`
 * first therefore does not produce a missing badge, which somebody would
 * notice; it produces a confidently wrong one, which nobody did until a game
 * of eight models all announced themselves as medium.
 *
 * `PlayerChip` had it right and `EndGameScreen` did not, which is the whole
 * argument for it being a function: the two are the same claim about the same
 * field, and a claim made twice is a claim that drifts.
 */
export function botLabel(player: PublicPlayer): string {
  if (player.botKind === 'llm') return 'ai';
  return player.botDifficulty ?? 'bot';
}
